/** Map pin model for the competitor map (HTML overlays + SVG cluster icons). */

/** own = your location · tracked = on your board · discoverable = nearby suggestion. */
export type CompetitorPinVariant = 'own' | 'discoverable' | 'tracked';

/** Relative standing vs your location rating — shown as a dot, not a fill. */
export type CompetitorPinTone = 'threat' | 'ahead' | 'even' | 'unknown';

/** Treat ratings within this gap as tied (map pins + vs-you chip). */
export const COMPETITOR_RATING_EPSILON = 0.05;

/** Cluster fills: brand blue when it holds a tracked competitor, else slate. */
const COLOR_CLUSTER_TRACKED = '#2563eb';
const COLOR_CLUSTER_SUGGESTED = '#64748b';
const COLOR_CARD = '#ffffff';

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

export function formatPinRating(rating?: number): string {
  if (rating == null || !Number.isFinite(rating)) return '–';
  return rating.toFixed(1);
}

export function pinToneVsYou(
  placeRating: number | undefined,
  yourRating: number | undefined,
): CompetitorPinTone {
  if (
    placeRating == null ||
    yourRating == null ||
    !Number.isFinite(placeRating) ||
    !Number.isFinite(yourRating)
  ) {
    return 'unknown';
  }
  const delta = placeRating - yourRating;
  if (delta > COMPETITOR_RATING_EPSILON) return 'threat';
  if (delta < -COMPETITOR_RATING_EPSILON) return 'ahead';
  return 'even';
}

/** Style bands: even → threat → ahead, each in 3 sizes. */
const CLUSTER_SIZES = [40, 48, 56] as const;

function clusterSizeBucket(count: number): 0 | 1 | 2 {
  if (count < 5) return 0;
  if (count < 12) return 1;
  return 2;
}

/** Count is painted inside the SVG so it cannot vanish like MarkerClusterer overlay text. */
export function buildClusterCountIcon(
  count: number,
  hasTracked: boolean,
): google.maps.Icon {
  const size = CLUSTER_SIZES[clusterSizeBucket(count)];
  const fill = hasTracked ? COLOR_CLUSTER_TRACKED : COLOR_CLUSTER_SUGGESTED;
  const fontSize = size <= 40 ? 14 : 16;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1.5}" fill="${COLOR_CARD}" />
  <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 4}" fill="${fill}" stroke="${COLOR_CARD}" stroke-width="2"/>
  <text x="${size / 2}" y="${size / 2 + fontSize * 0.35}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="${fontSize}" font-weight="700" fill="${COLOR_CARD}">${escapeXml(String(count))}</text>
</svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
}
